import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HexColorPicker } from 'react-colorful';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { sanitizeHexDraft, completeHexColor } from '@/lib/color/hexColorInput';
import { usePaletteColors } from '@/components/palette/PaletteContext';

interface ColorInputProps {
  value: string;
  onChange: (hex: string) => void;
  disabled?: boolean;
  testId?: string;
  className?: string;
  /** Optional quick-pick swatches shown above the picker (e.g. brand-kit palette). */
  presets?: string[];
}

/** The platform's one color input: a swatch button that opens a popover with a
 *  visual picker and a hex field. Radix Popover handles outside-click/Escape
 *  dismissal and portals to <body>, which the editor's layer outside-click
 *  guard already treats as "inside" (see isLayerUiTarget). */
export const ColorInput = ({ value, onChange, disabled, testId, className, presets }: ColorInputProps) => {
  const { t } = useTranslation();
  const contextPalette = usePaletteColors();
  const palette = presets ?? contextPalette;
  // null = mirror `value`; a string = what the user is currently typing.
  const [draft, setDraft] = useState<string | null>(null);
  const [otherOpen, setOtherOpen] = useState(true);

  const handleHexChange = (raw: string) => {
    const sanitized = sanitizeHexDraft(raw);
    if (sanitized === null) return;
    const complete = completeHexColor(sanitized);
    if (complete !== null) {
      setDraft(null);
      onChange(complete);
    } else {
      setDraft(sanitized);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (open) {
      const normalizedValue = value.toLowerCase();
      setOtherOpen(palette.length === 0 || !palette.some((color) => color.toLowerCase() === normalizedValue));
      return;
    }
    setDraft(null);
  };

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          disabled={disabled}
          aria-label={t('colorInput.openPicker')}
          className={cn(
            // block: an inline-block button would ride the same text line as a
            // preceding inline <Label> in the space-y-* label/field stacks.
            'block h-8 w-16 shrink-0 cursor-pointer rounded-md border border-border bg-background',
            'transition-colors hover:border-muted-foreground/60',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          style={{ backgroundColor: value }}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto space-y-2 p-3">
        {palette.length > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">{t('colorInput.palette')}</div>
            <div className="flex max-w-[200px] flex-wrap gap-1">
              {palette.map((preset, i) => (
                <button
                  key={`${preset}-${i}`}
                  type="button"
                  data-testid={testId ? `${testId}-preset-${i}` : undefined}
                  title={preset}
                  aria-label={preset}
                  className="h-8 w-8 cursor-pointer rounded border border-border transition-colors hover:border-muted-foreground/60"
                  style={{ backgroundColor: preset }}
                  onClick={() => onChange(preset.toLowerCase())}
                />
              ))}
            </div>
          </div>
        )}
        <button
          type="button"
          data-testid={testId ? `${testId}-other` : undefined}
          aria-expanded={otherOpen}
          className="text-xs font-medium text-foreground"
          onClick={() => setOtherOpen(true)}
        >
          {t('colorInput.other')}
        </button>
        {otherOpen && (
          <>
            <HexColorPicker color={value} onChange={onChange} />
            <Input
              type="text"
              data-testid={testId ? `${testId}-hex` : undefined}
              aria-label={t('colorInput.hexValue')}
              value={draft ?? value}
              onChange={(e) => handleHexChange(e.target.value)}
              onBlur={() => setDraft(null)}
              className="h-7 w-[200px] text-xs"
            />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default ColorInput;
